import { Link } from 'wouter';
import {
  MessageSquare,
  Database,
  Webhook,
  Code2,
  LineChart,
  Boxes,
  ArrowRight,
  Check,
  Cpu,
  Globe,
  Lock,
  Zap,
} from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

export function Product() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-bold text-slate-900 mb-6">
              Complete Time-Series Monitoring Platform
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              API-first platform for managing sensor data, IoT devices, and time-series
              metrics for you and your clients. Push, manage, and query data with isolated
              buckets and natural language controls.
            </p>
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-20 bg-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Feature 1: Natural Language */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-24">
            <div>
              <div className="inline-flex items-center space-x-2 bg-primary-50 text-primary-700 px-3 py-1 rounded-full mb-4 text-sm font-semibold">
                <MessageSquare className="w-4 h-4" />
                <span>Natural Language</span>
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Monitor Your Data Like You're Talking to a Human
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Skip the query languages and complex syntax. With dakkio's natural
                language interface, you can create alerts, query data, and get insights
                using plain English—just like talking to a colleague.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>Alert Creation:</strong> "Notify me if temperature exceeds
                    30°C for 10 minutes"
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>Data Queries:</strong> "Show me humidity trends from last
                    week"
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>Insights:</strong> "Which sensors had the highest variation
                    today?"
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8">
              <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
                <div className="flex items-center space-x-3 mb-4 pb-4 border-b border-slate-200">
                  <MessageSquare className="w-6 h-6 text-primary-600" />
                  <span className="font-semibold text-slate-900">Ask dakkio</span>
                </div>
                <div className="space-y-4">
                  <div className="bg-primary-50 rounded-lg p-4">
                    <p className="text-slate-900 text-sm">
                      "Alert me if battery level drops below 20% on any device"
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <p className="text-sm text-secondary-600 mb-2">✓ Alert created</p>
                    <p className="text-xs text-slate-600">
                      Monitoring 12 devices for battery level &lt; 20%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2: Time-Series Storage */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-24">
            <div className="order-2 md:order-1">
              <div className="bg-slate-50 rounded-2xl p-8">
                <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Database className="w-5 h-5 text-primary-600" />
                      <span className="font-semibold text-slate-900">Data Bucket</span>
                    </div>
                    <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-1 rounded">
                      Active
                    </span>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Data points:</span>
                      <span className="font-semibold text-slate-900">1.5M</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Sources:</span>
                      <span className="font-semibold text-slate-900">12</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Retention:</span>
                      <span className="font-semibold text-slate-900">90 days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Last data:</span>
                      <span className="font-semibold text-slate-900">2 min ago</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center space-x-2 bg-secondary-50 text-secondary-700 px-3 py-1 rounded-full mb-4 text-sm font-semibold">
                <Database className="w-4 h-4" />
                <span>Storage & Querying</span>
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Store Millions of Data Points, Query in Milliseconds
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                High-performance time-series database optimized for sensor data, metrics,
                and IoT applications. Automatic downsampling, aggregation, and retention
                management.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>Automatic downsampling:</strong> Query years of data without
                    performance impact
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>Flexible retention:</strong> 7 days to 10 years, configurable
                    per bucket
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>Multi-tenant isolation:</strong> Complete data separation for
                    B2B use cases
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Feature 3: Webhook Notifications */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-24">
            <div>
              <div className="inline-flex items-center space-x-2 bg-primary-50 text-primary-700 px-3 py-1 rounded-full mb-4 text-sm font-semibold">
                <Webhook className="w-4 h-4" />
                <span>Notifications</span>
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Get Notified When It Matters
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Flexible webhook system sends alerts to any HTTP endpoint. Integrate with
                Slack, Discord, email, SMS, or your custom notification system.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>Complex rules:</strong> Threshold, change detection, missing
                    data, composite conditions
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>Reliable delivery:</strong> Automatic retries with exponential
                    backoff
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>Cooldown periods:</strong> Prevent alert fatigue with
                    configurable delays
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-slate-900 rounded-2xl p-6 text-slate-100 font-mono text-sm">
              <div className="mb-3 text-slate-400"># Webhook Payload</div>
              <pre className="text-slate-100 overflow-x-auto">
{`{
  "event": "alert.triggered",
  "rule": "High Temperature",
  "dataSource": "Sensor-001",
  "trigger": {
    "value": 35.5,
    "threshold": 30,
    "timestamp": "2026-01-12T10:30:00Z"
  },
  "webhookUrl": "https://your-app.com/alerts"
}`}
              </pre>
            </div>
          </div>

          {/* Feature 4: Embeddable Widgets */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="bg-slate-50 rounded-2xl p-8">
                <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">
                        Temperature
                      </span>
                      <span className="text-sm font-semibold text-slate-900">23.5°C</span>
                    </div>
                    <div className="h-32 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-lg flex items-center justify-center">
                      <LineChart className="w-12 h-12 text-primary-600 opacity-40" />
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 mt-4">
                    <Code2 className="w-3 h-3 inline mr-1" />
                    Powered by dakkio
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center space-x-2 bg-secondary-50 text-secondary-700 px-3 py-1 rounded-full mb-4 text-sm font-semibold">
                <Code2 className="w-4 h-4" />
                <span>Widgets</span>
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Embed Beautiful Charts in Minutes
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Drop-in React components or simple script tags. Beautiful, responsive
                charts that work everywhere—your dashboard, customer portals, or public
                websites.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>Multiple chart types:</strong> Line, bar, area, gauge, and more
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>Fully customizable:</strong> Colors, themes, branding, and
                    white-label options
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>No backend needed:</strong> Widgets connect directly to dakkio
                    API
                  </span>
                </li>
              </ul>
              <div className="bg-slate-50 rounded-lg p-4 font-mono text-xs text-slate-700">
                <div className="text-slate-500 mb-2">// React Component</div>
                {`<DakkioChart
  bucketId="abc123"
  dataSourceIds={["sensor-1"]}
  timeRange="7d"
/>`}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Developer Features */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Built for Developers
            </h2>
            <p className="text-lg text-slate-600">
              Modern API, comprehensive SDKs, and documentation that doesn't waste your
              time.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <Zap className="w-10 h-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Multi-Tenant API</h3>
              <p className="text-slate-600 mb-4">
                Push, manage, and query data for you and your clients via REST API. Each client gets an isolated bucket with dedicated API keys. &lt;100ms latency, thousands of data points per second.
              </p>
              <a
                href="https://docs.dakkio.io/api"
                className="text-primary-600 hover:text-primary-700 font-semibold text-sm inline-flex items-center"
              >
                View API Docs
                <ArrowRight className="ml-1 w-4 h-4" />
              </a>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm">
              <Code2 className="w-10 h-10 text-secondary-600 mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Official SDKs
              </h3>
              <p className="text-slate-600 mb-4">
                TypeScript/JavaScript, Python, and Go SDKs with full TypeScript support and
                comprehensive type definitions.
              </p>
              <a
                href="https://docs.dakkio.io/sdks"
                className="text-primary-600 hover:text-primary-700 font-semibold text-sm inline-flex items-center"
              >
                Explore SDKs
                <ArrowRight className="ml-1 w-4 h-4" />
              </a>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm">
              <Boxes className="w-10 h-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Open Source Tools
              </h3>
              <p className="text-slate-600 mb-4">
                CLI tools, example projects, and integrations. All open source, all
                production-ready.
              </p>
              <a
                href="https://github.com/dakkio"
                className="text-primary-600 hover:text-primary-700 font-semibold text-sm inline-flex items-center"
              >
                View on GitHub
                <ArrowRight className="ml-1 w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Use Cases</h2>
            <p className="text-lg text-slate-600">
              From IoT devices to business metrics, dakkio handles any time-series data.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-50 rounded-xl p-8">
              <Globe className="w-10 h-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                IoT & Sensor Networks
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Monitor temperature, humidity, pressure, weight, and any sensor data.
                Perfect for smart buildings, agriculture, industrial IoT, and environmental
                monitoring.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-8">
              <Cpu className="w-10 h-10 text-secondary-600 mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Infrastructure Monitoring
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Track server metrics, application performance, database stats, and system
                health. Integrate with existing monitoring stacks via webhooks.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-8">
              <LineChart className="w-10 h-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Business Analytics
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Track KPIs, user metrics, sales data, and business trends. Embed charts in
                your dashboard or share with stakeholders.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-8">
              <Lock className="w-10 h-10 text-secondary-600 mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Embedded Analytics for B2B2C
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Use our API to manage data for each of your clients in isolated buckets. Push sensor data, configure alerts, and embed white-label dashboards—all via API. Perfect for IoT device manufacturers and SaaS platforms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-secondary-500">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            See dakkio in Action
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Start monitoring your data in minutes. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/signup">
              <a className="w-full sm:w-auto px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl flex items-center justify-center">
                Get Started Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Link>
            <Link href="/pricing">
              <a className="w-full sm:w-auto px-8 py-4 bg-primary-700 text-white rounded-lg font-semibold hover:bg-primary-800 transition-all border-2 border-white/20">
                View Pricing
              </a>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
